import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Layout from './components/Layout';
import ProtectedRoute from './components/ProtectedRoute';
import LoginPage from './pages/LoginPage';
import {
  ACCOUNT_ROUTE,
  AVAILABILITY_ROUTE,
  CALLER_ANALYSIS_ROUTE,
  CALLERS_ROUTE,
  CATEGORY_PAGES,
  DATABASE_ROUTE,
  RESUME_FILES_ROUTE,
  EMAIL_CHECKER_ROUTE,
  EMAIL_NOTICES_ROUTE,
  EMAIL_OUTCOME_RESULTS_ROUTE,
  EXTRACT_ROUTE,
  HOME_ROUTE,
  JOB_QUEUE_ROUTE,
  AUTOMATION_ROUTE,
  MEETINGS_MANAGE_ROUTE,
  MEETINGS_OUTLOOK_ROUTE,
  MEETINGS_ROUTE,
  SLACK_BID_RESULTS_ROUTE,
  TEMPLATE_ROUTE,
  USERS_REGISTER_ROUTE,
  TEAMS_ROUTE,
} from './constants';
import { defaultHomePath, useAuth } from './context/AuthContext';
import './App.css';
import './ui.css';

// Page chunks load on demand; LoginPage stays eager so the first paint has no fallback flash.
const ExtractPage = lazy(() => import('./pages/ExtractPage'));
const CategoryPage = lazy(() => import('./pages/CategoryPage'));
const DatabasePage = lazy(() => import('./pages/DatabasePage'));
const ResumeFilesPage = lazy(() => import('./pages/ResumeFilesPage'));
const AvailabilityPage = lazy(() => import('./pages/AvailabilityPage'));
const CallersPage = lazy(() => import('./pages/CallersPage'));
const JobQueuePage = lazy(() => import('./pages/JobQueuePage'));
const AutomationPage = lazy(() => import('./pages/AutomationPage'));
const CallerAnalysisPage = lazy(() => import('./pages/CallerAnalysisPage'));
const MeetingDetailPage = lazy(() => import('./pages/MeetingDetailPage'));
const InterviewCalendarPage = lazy(() => import('./pages/InterviewCalendarPage'));
const OutlookCheckPage = lazy(() => import('./pages/OutlookCheckPage'));
const InterviewSchedulePage = lazy(() => import('./pages/InterviewSchedulePage'));
const MeetingsPage = lazy(() => import('./pages/MeetingsPage'));
const HomePage = lazy(() => import('./pages/HomePage'));
const RegisterUserPage = lazy(() => import('./pages/RegisterUserPage'));
const TeamsPage = lazy(() => import('./pages/TeamsPage'));
const AccountPage = lazy(() => import('./pages/AccountPage'));
const BidResultsPage = lazy(() => import('./pages/BidResultsPage'));
const EmailCheckerPage = lazy(() => import('./pages/EmailCheckerPage'));
const EmailNoticesPage = lazy(() => import('./pages/EmailNoticesPage'));
const EmailOutcomeResultsPage = lazy(() => import('./pages/EmailOutcomeResultsPage'));
const TemplatePage = lazy(() => import('./pages/TemplatePage'));

function HomeRedirect() {
  const { user } = useAuth();
  return <Navigate to={defaultHomePath(user)} replace />;
}

function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route
          path="/*"
          element={(
            <ProtectedRoute>
              <Layout>
                <Suspense fallback={<div className="page-loading">Loading…</div>}>
                  <Routes>
                    <Route path={HOME_ROUTE.path} element={<HomePage />} />
                    <Route path="/welcome" element={<Navigate to={HOME_ROUTE.path} replace />} />
                    <Route path={EXTRACT_ROUTE.path} element={<ExtractPage />} />
                    <Route path={JOB_QUEUE_ROUTE.path} element={<JobQueuePage />} />
                    <Route path={AUTOMATION_ROUTE.path} element={<AutomationPage />} />
                    {CATEGORY_PAGES.map((cat) => (
                      <Route key={cat.id} path={cat.path} element={<CategoryPage category={cat} />} />
                    ))}
                    <Route path={TEMPLATE_ROUTE.path} element={<TemplatePage />} />
                    <Route path={DATABASE_ROUTE.path} element={<DatabasePage />} />
                    <Route path={RESUME_FILES_ROUTE.path} element={<ResumeFilesPage />} />
                    <Route path={USERS_REGISTER_ROUTE.path} element={<RegisterUserPage />} />
                    <Route path={TEAMS_ROUTE.path} element={<TeamsPage />} />
                    <Route path={ACCOUNT_ROUTE.path} element={<AccountPage />} />
                    <Route path={SLACK_BID_RESULTS_ROUTE.path} element={<BidResultsPage />} />
                    <Route path={EMAIL_CHECKER_ROUTE.path} element={<EmailCheckerPage />} />
                    <Route path={EMAIL_NOTICES_ROUTE.path} element={<EmailNoticesPage />} />
                    <Route path={CALLER_ANALYSIS_ROUTE.path} element={<CallerAnalysisPage />} />
                    <Route path={CALLERS_ROUTE.path} element={<CallersPage />} />
                    <Route path={AVAILABILITY_ROUTE.path} element={<AvailabilityPage />} />
                    <Route path={`${MEETINGS_ROUTE.path}/calendar`} element={<InterviewCalendarPage />} />
                    <Route path={MEETINGS_MANAGE_ROUTE.path} element={<MeetingsPage />} />
                    <Route path={MEETINGS_OUTLOOK_ROUTE.path} element={<OutlookCheckPage />} />
                    <Route path={EMAIL_OUTCOME_RESULTS_ROUTE.path} element={<EmailOutcomeResultsPage />} />
                    <Route path={`${MEETINGS_ROUTE.path}/:id`} element={<MeetingDetailPage />} />
                    <Route path={MEETINGS_ROUTE.path} element={<InterviewSchedulePage />} />
                    <Route path="*" element={<HomeRedirect />} />
                  </Routes>
                </Suspense>
              </Layout>
            </ProtectedRoute>
          )}
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
